// GlassQuickPanel — Builder 2 (Sprint UX + Legal).
//
// A lightweight, glass-morphism quick-action row that lives ABOVE the hero
// on Home. It exposes secondary features that don't belong in the strict
// 5-tab BottomNav (Home / Closet / Stylist / Social / Profile).
//
// Feature-flag / role rules (see coordination spec):
//   - Planner: disabled "coming soon" (no route yet).
//   - Academy: disabled "coming soon" (no route yet).
//   - Pros:    only if `style_services_v1` flag on OR admin/founder.
//   - Shop:    only if `shop_v1` flag on.
//   - Invites: /sharing (link exists for all authed users).
//   - Founder Console: only for admin/founder.
//
// Auto-hide contract:
//   - Hidden when scrollY > 24 AND direction=down.
//   - Reappears when scrollY <= 24 OR direction=up.
//   - Hidden state uses transform+opacity+pointer-events:none.
//   - Scroll listener is passive + rAF-throttled (no debounce timer leaks).
//   - Respects `prefers-reduced-motion`.
//
// Accessibility:
//   - Buttons expose `aria-label`; disabled tiles have `aria-disabled`.
//   - The panel is `role="region"` with a discoverable name.
//   - When hidden it also becomes `aria-hidden="true"` so screen readers
//     don't announce it.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

const Tile = ({ label, sub, onClick, disabled, badge, testId }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    aria-disabled={disabled || undefined}
    aria-label={label + (sub ? ` — ${sub}` : '')}
    data-testid={testId}
    className={[
      'flex-shrink-0 basis-[46%] sm:basis-[30%] md:basis-[22%] min-w-[9.5rem] max-w-[13rem]',
      'text-left rounded-2xl px-4 py-3',
      'backdrop-blur-md',
      'transition-all duration-200',
      disabled
        ? 'bg-white/40 border border-white/40 opacity-60 cursor-not-allowed'
        : 'bg-white/55 border border-white/60 hover:bg-white/80 hover:border-[#C7A96B]/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D0D0D]',
    ].join(' ')}
  >
    <div className="flex items-center justify-between gap-2 mb-0.5">
      <p className="font-serif text-sm text-[#0D0D0D] leading-tight truncate">{label}</p>
      {badge && (
        <span className="font-sans text-[9px] uppercase tracking-[0.18em] text-[#8E8A84] flex-shrink-0">
          {badge}
        </span>
      )}
    </div>
    <p className="font-sans text-[11px] text-[#8E8A84] leading-snug line-clamp-1">{sub}</p>
  </button>
);

export const GlassQuickPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const shopFlag = useFeatureFlag('shop_v1');
  const proFlag = useFeatureFlag('style_services_v1');

  // Server-issued role only — never an email string (v4.3.1 P0 parity with backend).
  const isFounder = user?.role === 'founder';
  const isAdmin = user?.role === 'admin' || isFounder || user?.is_admin === true;

  // Actions — order matters. Disabled tiles for surfaces without routes.
  const actions = useMemo(() => {
    const items = [
      {
        key: 'planner',
        label: 'Planner',
        sub: 'Plan looks for the week',
        onClick: () => navigate('/planner'),
      },
      {
        key: 'academy',
        label: 'Academy',
        sub: 'Learn your style',
        onClick: () => navigate('/style-academy'),
      },
    ];
    if (proFlag.on || isAdmin) {
      items.push({
        key: 'pros',
        label: 'Pros',
        sub: 'Stylists & designers',
        onClick: () => navigate('/marketplace'),
      });
    }
    if (shopFlag.on) {
      items.push({
        key: 'shop',
        label: 'Shop',
        sub: 'Curated inspiration',
        onClick: () => navigate('/shop'),
      });
    }
    // Invites is available to any authed user via /sharing.
    items.push({
      key: 'invites',
      label: 'Invites',
      sub: 'Share your access',
      onClick: () => navigate('/sharing'),
    });
    // Mode-UX Phase 5 — the "All features" launcher is available in EVERY
    // experience mode, so no mode can hide a capability irrecoverably.
    items.push({
      key: 'all-features',
      label: 'All features',
      sub: 'Everything in one place',
      onClick: () => navigate('/features'),
    });
    // Admin / founder tile — never shown to regular users.
    if (isAdmin) {
      items.push({
        key: 'founder',
        label: 'Founder Console',
        sub: 'Admin overview',
        badge: 'Admin',
        onClick: () => navigate('/founder/crm'),
      });
    }
    return items;
  }, [shopFlag.on, proFlag.on, isAdmin, navigate]);

  // ----- Auto-hide on scroll (down hides, up/top reveals). -----
  const [hidden, setHidden] = useState(false);
  const rafRef = useRef(0);
  const lastYRef = useRef(0);
  const reduceMotionRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    reduceMotionRef.current = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;

    const onScroll = () => {
      if (rafRef.current) return; // rAF throttle — coalesce bursts into one frame.
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = 0;
        const y = window.scrollY || window.pageYOffset || 0;
        const last = lastYRef.current;
        // Threshold 24px — anything under counts as "at top".
        if (y <= 24) {
          setHidden(false);
        } else if (y > last + 4) {
          setHidden(true); // scrolling down
        } else if (y < last - 4) {
          setHidden(false); // scrolling up
        }
        lastYRef.current = y;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Prime initial state.
    lastYRef.current = window.scrollY || 0;
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const transformStyle = hidden
    ? { transform: 'translate3d(0, -12px, 0)', opacity: 0 }
    : { transform: 'translate3d(0, 0, 0)', opacity: 1 };
  const transition = reduceMotionRef.current ? 'none' : 'transform 220ms ease, opacity 220ms ease';

  return (
    <section
      aria-label="Quick actions"
      aria-hidden={hidden || undefined}
      data-testid="glass-quick-panel"
      data-hidden={hidden ? 'true' : 'false'}
      className="w-full max-w-3xl mx-auto"
      style={{
        ...transformStyle,
        transition,
        pointerEvents: hidden ? 'none' : 'auto',
        willChange: 'transform, opacity',
      }}
    >
      <div className="page-pad pt-3 pb-1">
        <div
          className="flex gap-2 overflow-x-auto no-scrollbar py-1"
          data-testid="glass-quick-panel-list"
        >
          {actions.map(a => (
            <Tile
              key={a.key}
              label={a.label}
              sub={a.sub}
              onClick={a.onClick}
              disabled={a.disabled}
              badge={a.badge}
              testId={`glass-panel-${a.key}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default GlassQuickPanel;
